/**
 * Related-work anchor gates + scoring.
 *
 * Finds the prior sessions most related to a query session by SHARED FILE
 * ANCHORS — paths both sessions touched — with the precision gates the
 * promptman research program measured (ungated shared paths ≈ coin flip;
 * gated ≈ 84–93% precise):
 *
 *   1. File paths only, suffix-normalized to their last 3 segments. Paths
 *      with fewer than 3 segments are dropped (repo roots and bare names
 *      match everything), as are glob patterns and directories — see
 *      `anchorKeyForPath`.
 *   2. Fanout band: an anchor's fanout = distinct sessions touching it. Keep
 *      fanout in [2, bandMaxForSessionCount(N)].
 *   3. Anchor weight = ln(N_sessions / fanout) — rarer anchors count more.
 *
 * Pure functions over plain data; no I/O. The rows come from queries.js
 * (server-extracted resource_paths — noisier than dedicated extractors,
 * which is exactly why the gates are not optional).
 */

const SUFFIX_SEGMENTS = 3;
const SAME_THREAD_MS = 7 * 86400 * 1000;
export const MAX_SUGGESTIONS = 5;

// Extensionless names that are real files. Everything else whose last segment
// carries no dot is a directory: `crates/api-server/src` reads as an anchor but
// is touched by any session working anywhere in that tree, and directories were
// 26% of the shared-anchor population on the corpus this was measured against.
const EXTENSIONLESS_FILES = new Set([
    "makefile", "dockerfile", "containerfile", "justfile", "rakefile", "gemfile",
    "procfile", "brewfile", "vagrantfile", "license", "licence", "readme",
    "changelog", "notice", "authors", "codeowners", "owners",
]);

/**
 * Suffix anchor for a path-like value, or null when it fails the shape gate.
 *
 * @param {string} path
 * @returns {{ key: string, suffix: string } | null} `key` is the lowercased
 *   last-3-segment identity used for matching; `suffix` is the same segments in
 *   their ORIGINAL case, which is what a server-side LIKE has to be built from
 *   (the ngram prefilter backing that column is case-sensitive, so a lowercased
 *   pattern would silently match nothing).
 */
export function anchorForPath(path) {
    if (typeof path !== "string") return null;
    const raw = path.trim();
    if (!raw || /\s/.test(raw)) return null;
    if (raw.includes("://")) return null;
    // `*/node_modules/*`, `src/**/*.rs` — search expressions the agent passed to
    // a tool, not artifacts it touched. They pass every other gate (three
    // segments, an extension) and read as shared evidence for any two sessions
    // that ran the same glob.
    if (raw.includes("*") || raw.includes("?")) return null;
    const segments = raw.split(/[/\\]+/).filter(Boolean);
    if (segments.length < SUFFIX_SEGMENTS) return null;
    const last = segments[segments.length - 1];
    if (!last.includes(".") && !EXTENSIONLESS_FILES.has(last.toLowerCase())) return null;
    const suffix = segments.slice(-SUFFIX_SEGMENTS).join("/");
    return { key: suffix.toLowerCase(), suffix };
}

export function anchorKeyForPath(path) {
    return anchorForPath(path)?.key ?? null;
}

/**
 * Upper fanout cut: an anchor touched by more than this many sessions says
 * nothing about relatedness.
 *
 * This replaces a P99 taken over the whole tenant's anchor-fanout distribution,
 * which required holding every (session, path) pair in the browser. The
 * distribution is not available per-trace and cannot be reconstructed from a
 * sample (the fanout >= 2 eligibility filter enriches any sample in widespread
 * anchors — a 1/4 sample overestimates by 56–100%), so the cut is fitted
 * instead. Measured P99 against session count on the reference corpus:
 *
 *   N =  806 → 30    N = 1237 → 36    N = 1956 → 48    N = 1981 → 48
 *
 * 1.05 * sqrt(N) reproduces those within one or two. It is a fit over four
 * points from ONE tenant, which is the honest status of this constant: the cut
 * no longer adapts to how a tenant's file popularity is actually shaped, only
 * to how many sessions it has.
 */
const BAND_K = 1.05;

export function bandMaxForSessionCount(sessionCount) {
    return Math.max(2, Math.round(BAND_K * Math.sqrt(Math.max(sessionCount, 2))));
}

/**
 * Build the gated anchor index from (sessionId, path) rows.
 *
 * @param {Array<{ sid: string, path: string }>} pairs rows from the anchor
 *   neighbourhood query — every session touching any of `anchorKeys`, with the
 *   full path list of each matching prompt.
 * @param {object} opts
 * @param {number} opts.sessionCount total sessions in the lookback window (N),
 *   from a separate scalar read. NOT derivable from `pairs`, which only covers
 *   the neighbourhood.
 * @param {Set<string>} opts.anchorKeys the query session's own anchor keys.
 *   Anchors outside this set are dropped: `pairs` only contains sessions that
 *   matched one of these, so any OTHER key appearing in it has an undercounted
 *   fanout and would be weighted as far rarer than it is.
 * @returns {{
 *   anchors: Map<string, { sessions: Set<string>, weight: number, pathBySid: Map<string, string> }>,
 *   sessionCount: number,
 *   bandMax: number,
 * }}
 */
export function buildAnchorIndex(pairs, { sessionCount, anchorKeys }) {
    const bandMax = bandMaxForSessionCount(sessionCount);
    const bySuffix = new Map(); // key → { sessions:Set, pathBySid:Map }
    for (const { sid, path } of pairs) {
        if (!sid) continue;
        const key = anchorKeyForPath(path);
        if (!key || !anchorKeys.has(key)) continue;
        let entry = bySuffix.get(key);
        if (!entry) {
            entry = { sessions: new Set(), pathBySid: new Map() };
            bySuffix.set(key, entry);
        }
        entry.sessions.add(sid);
        if (!entry.pathBySid.has(sid)) entry.pathBySid.set(sid, path);
    }

    const anchors = new Map();
    for (const [key, entry] of bySuffix) {
        const fanout = entry.sessions.size;
        // An anchor touched by EVERY session drops regardless of the band: its
        // weight ln(N/N) = 0 says it distinguishes nothing.
        if (fanout < 2 || fanout > bandMax || fanout >= sessionCount) continue;
        anchors.set(key, {
            sessions: entry.sessions,
            pathBySid: entry.pathBySid,
            weight: Math.log(Math.max(sessionCount, 2) / fanout),
        });
    }
    return { anchors, sessionCount, bandMax };
}

/**
 * Score prior sessions against the query session over a gated anchor index.
 *
 * @param {object} args
 * @param {ReturnType<typeof buildAnchorIndex>} args.index
 * @param {string} args.querySessionId
 * @param {Array<string>} args.queryPaths raw resource paths of the query session.
 * @param {Map<string, { tsMinMs: number, tsMaxMs: number, endpointId: string|null }>} args.sessions
 * @param {number} args.queryTsMs the query session's start (fallback: trace time).
 * @returns {Array<{
 *   sessionId: string, score: number, tsMaxMs: number, endpointId: string|null,
 *   sameThread: boolean,
 *   shared: Array<{ key: string, weight: number, fanout: number, queryPath: string, candidatePath: string }>,
 * }>} top MAX_SUGGESTIONS, best first.
 */
export function scoreRelated({ index, querySessionId, queryPaths, sessions, queryTsMs }) {
    const queryKeys = new Map(); // key → query's own full path (verbatim)
    for (const path of queryPaths || []) {
        const key = anchorKeyForPath(path);
        if (key && index.anchors.has(key) && !queryKeys.has(key)) queryKeys.set(key, path);
    }
    if (!queryKeys.size) return [];

    const meta = (sid) => sessions.get(sid);
    const queryMeta = meta(querySessionId);
    const queryStartMs = queryMeta?.tsMinMs ?? queryTsMs;

    const bySession = new Map(); // sid → { score, shared:[] }
    for (const [key, queryPath] of queryKeys) {
        const anchor = index.anchors.get(key);
        for (const sid of anchor.sessions) {
            if (sid === querySessionId) continue;
            const m = meta(sid);
            // Prior work only: sessions that STARTED before this one.
            if (!m || !(m.tsMinMs < queryStartMs)) continue;
            let cand = bySession.get(sid);
            if (!cand) {
                cand = { sessionId: sid, score: 0, shared: [], tsMaxMs: m.tsMaxMs, endpointId: m.endpointId };
                bySession.set(sid, cand);
            }
            cand.score += anchor.weight;
            cand.shared.push({
                key,
                weight: anchor.weight,
                fanout: anchor.sessions.size,
                queryPath,
                candidatePath: anchor.pathBySid.get(sid) ?? key,
            });
        }
    }

    const out = [...bySession.values()];
    out.sort((a, b) => (b.score - a.score) || (b.tsMaxMs - a.tsMaxMs));
    for (const cand of out) {
        cand.shared.sort((a, b) => b.weight - a.weight);
        cand.sameThread = queryStartMs - cand.tsMaxMs <= SAME_THREAD_MS;
    }
    return out.slice(0, MAX_SUGGESTIONS);
}

/**
 * The distinct suffixes to search on, best-first.
 *
 * Ranking is load-bearing rather than cosmetic: the caller truncates this list
 * to a needle cap, so position decides which of a session's anchors are looked
 * for at all. A path touched by more of the session's prompts is more central
 * to what it was doing, so it survives; ties break on the suffix so the same
 * session always issues the same request (and so hits the query cache).
 *
 * Deduped by SUFFIX, not by key: two case variants of one path are one anchor
 * for matching but two distinct server-side patterns, since the LIKE that
 * finds them is case-sensitive.
 *
 * @param {Array<{ path: string, prompts: number }>} pathRows
 * @returns {Array<{ suffix: string, key: string }>}
 */
export function rankNeedles(pathRows) {
    const bySuffix = new Map(); // suffix → { key, prompts }
    for (const { path, prompts } of pathRows || []) {
        const anchor = anchorForPath(path);
        if (!anchor) continue;
        const prev = bySuffix.get(anchor.suffix);
        if (!prev || prompts > prev.prompts) bySuffix.set(anchor.suffix, { key: anchor.key, prompts });
    }
    return [...bySuffix.entries()]
        .sort((a, b) => (b[1].prompts - a[1].prompts) || a[0].localeCompare(b[0]))
        .map(([suffix, v]) => ({ suffix, key: v.key }));
}

/**
 * Candidate sessions in `pairs`, ranked by a crude shared-anchor count.
 *
 * Session metadata is fetched by id (bounded IN list), so the neighbourhood has
 * to be narrowed before that read when it is large. Ranking by raw overlap is
 * only used to choose WHICH sessions to look up, never to order suggestions.
 *
 * @returns {Array<string>} candidate session ids, most overlap first.
 */
export function rankCandidateSessions(pairs, anchorKeys, querySessionId) {
    const hits = new Map();
    for (const { sid, path } of pairs) {
        if (!sid || sid === querySessionId) continue;
        const key = anchorKeyForPath(path);
        if (!key || !anchorKeys.has(key)) continue;
        let seen = hits.get(sid);
        if (!seen) {
            seen = new Set();
            hits.set(sid, seen);
        }
        seen.add(key);
    }
    return [...hits.entries()]
        .sort((a, b) => b[1].size - a[1].size)
        .map(([sid]) => sid);
}
